import { lodash } from '@avantia/lodash';
import { HpError } from '@avantia/logging-and-errors';
import { CSSProperties } from 'react';

export interface HpImageWidgetProps {
  altText: string;
  className?: string;
  image: string;
  selected: boolean;
  style?: CSSProperties;
}

export function ImageWidget({ altText, className, image, selected, style }: HpImageWidgetProps): JSX.Element {
  let imageSource = '';
  if (image) {
    const { path, imageResolved } = resolveImageTemplate(image, selected);
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    const data = require(`../../../assets/${path}/${imageResolved}`);
    if (typeof data === 'string') {
      imageSource = data;
    } else if (data && data.default) {
      imageSource = data.default;
    }
  }

  return (
    <img
      alt={altText}
      className={`hp-image ${className || 'hp-img-thumbnail'}`}
      src={imageSource}
      style={style}
      title={altText}
    />
  );
}

export interface HpResolveImageTemplateResult {
  imageResolved: string;
  path: 'svg' | 'images';
}

export function resolveImageTemplate(image: string, selected: boolean): HpResolveImageTemplateResult {
  const ix = image.lastIndexOf('.');
  const codeStart = image.indexOf('{{');
  const codeEnd = codeStart ? image.indexOf('}}', codeStart) : -1;
  if (codeStart >= 0 && codeEnd >= 0) {
    image =
      image.substring(0, codeStart) +
      evaluateImageCode(image.substring(codeStart + 2, codeStart + codeEnd - codeStart), { selected }) +
      image.substring(codeEnd + 2);
  }

  const extn = ix >= 0 ? image.substring(image.lastIndexOf('.') + 1) : '';
  const path = extn === 'svg' ? 'svg' : 'images';
  return { path, imageResolved: image };
}

function evaluateImageCode(code: string, model: Record<string, boolean>): string {
  if (code === '') {
    return '';
  }

  code = code.replace(/ /g, '');
  const [condition, rest] = splitStringAt(code, '?');
  if (rest) {
    const [truePart, falsePart] = splitStringAt(rest, ':');
    if (Object.prototype.hasOwnProperty.call(model, condition)) {
      return model[condition] === true ? truePart : falsePart || '';
    }

    throw new HpError(
      `The image conditional expression must correspond with a boolean property in the model: ['${lodash
        .keys(model)
        .join("', '")}']. Instead found: "${condition}".`
    );
  }

  throw new HpError(
    `The image conditional expression must contain a condition followed by a '?'. Instead found: "${code}".`
  );
}

function splitStringAt(text: string, breakAt: string): [top: string, rest: string | undefined] {
  const ix = text.indexOf(breakAt);
  if (ix < 0) {
    return [text, undefined];
  }

  return [text.substring(0, ix), text.substring(ix + breakAt.length)];
}
